import React, { useEffect, useMemo, useRef } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';

import CounterWithRefresh from '~/components/core/CounterWithRefresh/CounterWithRefresh';
import SidePanelCardDialog from '~/components/core/SidePanelCardDialog/SidePanelCardDialog';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import type { PaginationProps } from '~/components/core/Tables/SortableTable/TablePaginationSection/types';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

import { useClaim } from '../ClaimContainer';

import CommunicationFilters from './CommunicationFilters';
import CommunicationsTable from './CommunicationsTable';
import { getAllSearchableContactRoles } from './ContactUtils';
import type { ChannelTabType, Communication, CommunicationChannelKey, HandledStatusFilter } from './types';
import ViewCommunicationCardContainer from './ViewCommunicationCardContainer';

const DEFAULT_ROWS_PER_PAGE = 20;

interface SortByColumn {
  id: string;
  order: 'asc' | 'desc';
}

interface PaginationState {
  page: number;
  rowsPerPage: number;
  totalCount: number;
  resultsTruncated: boolean;
  setPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
}

export interface CommunicationsTablePanelProps {
  channelType: ChannelTabType;
  displayLoading: boolean;
  isLoading: boolean;
  isError: boolean;
  communications?: Communication[];
  selectedCommunicationId?: number | null;
  onSelectCommunication: (communicationId: number | null) => void;
  pagination: PaginationState;
  count?: number;
  unreadRowsIds?: number[];
  onRefresh: () => void;
  onRefreshKeepingPage: () => Promise<unknown>;
  onRefreshCurrentPage?: () => Promise<unknown>;
  onUpdate: () => void;
  commsPerPage?: number;
  onTabChange?: (() => void) | null;
  onSortByColumn?: ((event: unknown, sortByColumn: SortByColumn) => void) | null;
  sortByColumn?: SortByColumn | null;
  addSubjectColumn?: boolean;
  removeExposuresColumn?: boolean;
  removeAdjusterColumn?: boolean;
  addClaimColumn?: boolean;
  showClive?: boolean;
  // Filters props (moved from parent)
  showFilters?: boolean;
  selectedContact?: ContactFullModel | null;
  onSelectContact?: (contact: ContactFullModel | null) => void;
  selectedAdjuster?: { id: string; username: string } | null;
  onSelectAdjuster?: (adjuster: { id: string; username: string } | null) => void;
  communicationAdjusters?: { id: string; username: string }[];
  filteredExposuresIds?: number[];
  onUpdateFilteredExposures?: (exposuresIds: number[]) => void;
  onClearFilters?: () => void;
  organizationContactRolesDict?: Record<string, unknown>;
  availableChannels?: ChannelTabType[];
  selectedChannelsFilter?: CommunicationChannelKey[] | null;
  onChangeSelectedChannelsFilter?: (channels: CommunicationChannelKey[] | null) => void;
  displayAdjusterFilter?: boolean;
  displayExposuresFilter?: boolean;
  searchText?: string;
  onSearchTextChange?: (searchText: string) => void;
  onClearText?: () => void;
  selectedCategories?: number[];
  onChangeSelectedCategories?: (categoryIds: number[]) => void;
  isHandledStatusFilterAvailable?: boolean;
  handledStatusFilter?: HandledStatusFilter;
  onChangeHandledStatusFilter?: (value: HandledStatusFilter) => void;
  isHandledStatusDefaultState?: boolean;
}

const CommunicationsTablePanel: React.FC<CommunicationsTablePanelProps> = ({
  channelType,
  displayLoading,
  isLoading,
  isError,
  communications = [],
  selectedCommunicationId = null,
  onSelectCommunication,
  pagination,
  count = 0,
  unreadRowsIds = [],
  onRefresh,
  onRefreshKeepingPage,
  onRefreshCurrentPage,
  onUpdate,
  commsPerPage = DEFAULT_ROWS_PER_PAGE,
  onSortByColumn = null,
  sortByColumn = null,
  addSubjectColumn = false,
  removeExposuresColumn = false,
  removeAdjusterColumn = false,
  addClaimColumn = false,
  showClive = false,
  // Filters props
  showFilters = false,
  selectedContact,
  onSelectContact,
  selectedAdjuster,
  onSelectAdjuster,
  communicationAdjusters,
  filteredExposuresIds,
  onUpdateFilteredExposures,
  onClearFilters,
  organizationContactRolesDict,
  availableChannels,
  selectedChannelsFilter,
  onChangeSelectedChannelsFilter,
  displayAdjusterFilter,
  displayExposuresFilter,
  searchText,
  onSearchTextChange,
  onClearText,
  selectedCategories = [],
  onChangeSelectedCategories,
  isHandledStatusFilterAvailable,
  handledStatusFilter,
  onChangeHandledStatusFilter,
  isHandledStatusDefaultState,
}) => {
  const { claim } = useClaim();
  const isInitialMount = useRef(true);

  const areFiltersReady = useMemo((): boolean => {
    return !!(
      onSelectContact &&
      onSelectAdjuster &&
      communicationAdjusters &&
      filteredExposuresIds !== undefined &&
      onUpdateFilteredExposures &&
      onClearFilters &&
      organizationContactRolesDict &&
      availableChannels &&
      onChangeSelectedChannelsFilter
    );
  }, [
    onSelectContact,
    onSelectAdjuster,
    communicationAdjusters,
    filteredExposuresIds,
    onUpdateFilteredExposures,
    onClearFilters,
    organizationContactRolesDict,
    availableChannels,
    onChangeSelectedChannelsFilter,
  ]);

  const paginationProps: PaginationProps = {
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    onChangePage: (_: unknown, newPage: number) => pagination.setPage(newPage),
    count: count || 0,
    rowsPerPageOptions: [],
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    void onRefreshKeepingPage();
  }, [claim, onRefreshKeepingPage]);

  return (
    <>
      <PanelGroup
        direction="horizontal"
        id="group-adjuster-communications"
        autoSaveId="communications-panel-layout"
        className="h-full"
      >
        <Panel id="table-display" defaultSize={selectedCommunicationId ? 60 : 100} style={{ overflow: 'auto' }}>
          {showFilters && areFiltersReady ? (
            <CommunicationFilters
              selectedContact={selectedContact ?? null}
              onSelectContact={onSelectContact!}
              selectedAdjuster={selectedAdjuster ?? null}
              onSelectAdjuster={onSelectAdjuster!}
              communicationAdjusters={communicationAdjusters!}
              filteredExposuresIds={filteredExposuresIds!}
              onUpdateFilteredExposures={onUpdateFilteredExposures!}
              onClearFilters={onClearFilters!}
              organizationContactRolesDict={organizationContactRolesDict!}
              getAllSearchableContactRoles={getAllSearchableContactRoles}
              isLoading={isLoading}
              activeChannelType={channelType}
              availableChannels={availableChannels!}
              selectedChannelsFilter={selectedChannelsFilter ?? null}
              onChangeSelectedChannelsFilter={onChangeSelectedChannelsFilter!}
              displayAdjusterFilter={Boolean(displayAdjusterFilter)}
              displayExposuresFilter={Boolean(displayExposuresFilter)}
              searchText={searchText}
              onSearchTextChange={onSearchTextChange}
              onClearText={onClearText}
              selectedCategories={selectedCategories}
              onChangeSelectedCategories={onChangeSelectedCategories}
              isHandledStatusFilterAvailable={isHandledStatusFilterAvailable}
              handledStatusFilter={handledStatusFilter}
              onChangeHandledStatusFilter={onChangeHandledStatusFilter}
              isHandledStatusDefaultState={isHandledStatusDefaultState}
            />
          ) : null}
          {!displayLoading ? (
            <CommunicationsTable
              communications={communications}
              selectedCommunicationId={selectedCommunicationId}
              onSelectCommunication={onSelectCommunication}
              paginationProps={paginationProps}
              removeExposuresColumn={removeExposuresColumn}
              removeAdjusterColumn={removeAdjusterColumn}
              addClaimColumn={addClaimColumn}
              addSubjectColumn={addSubjectColumn}
              showClive={showClive}
              onRefreshCurrentPage={onRefreshCurrentPage}
              // @ts-expect-error - CommunicationsTable is JSX, TypeScript can't infer proper prop types
              unreadRowsIds={unreadRowsIds}
              onSortByColumn={onSortByColumn}
              sortByColumn={sortByColumn}
              counterViewText={
                <div className="pl-12">
                  <CounterWithRefresh
                    onRefresh={onRefresh}
                    isLoading={isLoading}
                    pagination={pagination}
                    refreshTooltipText={`Refresh ${channelType} communications`}
                    refreshAriaLabel={`Refresh ${channelType} communications`}
                  />
                </div>
              }
            />
          ) : (
            <SkeletonTable rowsCount={commsPerPage} columnsCount={5} maxHeight="45vh" isError={isError} />
          )}
        </Panel>
        {selectedCommunicationId && (
          <SidePanelCardDialog
            panelId="comm-display"
            headerStyle={{
              padding: '0',
            }}
            contentStyle={{
              padding: '0',
            }}
            containerStyle={{
              display: 'grid',
              // 👇 important for keeping child items from overflowing the container
              // 👇 and allowing horizontal scrolling in communication content, if needed
              gridTemplateColumns: '100%',
              minHeight: '100%',
            }}
          >
            <ViewCommunicationCardContainer
              key={selectedCommunicationId}
              communicationId={selectedCommunicationId}
              onUpdate={onUpdate}
              onRefreshCurrentPage={onRefreshCurrentPage}
              displayAttachClaim={false}
              showClive={showClive}
              onClose={() => onSelectCommunication(null)}
              cardDialogContainerStyle={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            />
          </SidePanelCardDialog>
        )}
      </PanelGroup>
    </>
  );
};

export default CommunicationsTablePanel;
